import React, { useCallback, useLayoutEffect } from 'react';
import { ScrollView, View } from 'react-native';
import Choices from 'design-system/Choices';
import StatusBar from 'design-system/StatusBar';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import Sentry from 'utils/sentry';
import useColors from 'hooks/useColors';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { fundingSourceMap, fundingSources } from 'features/borrow/monevo/models';
import { OnboardingFlow } from 'utils/types/navigation';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import useNavigateToNextScreenForPotsOnboarding from 'features/pots/hooks/useNavigateToNextScreenForPotsOnboarding';
import LeaveOnboardingButton from 'features/wealth/components/LeaveOnboardingButton';
import { useCommonInvestStyles } from './styles';
import { useAmplitudeOnMountEvent } from '../utils/analytics';
import InvestHeaderWithHelpButton from '../components/InvestHeaderWithHelpButton';
import { strings as onboardingTitleStrings } from '../components/OnboardingTitle';
import useNavigateToNextScreen from '../hooks/useNavigateToNextScreen';
import { selectAnnualIncome, selectNetworth } from '../reducers/selectors';
const CHOICES = fundingSources;
const FundingSourceScreen = ({ navigation, route: { params } }) => {
    const colors = useColors();
    const commonStyles = useCommonInvestStyles(params?.mountedOnAnotherScreen);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
            headerRight: () => (<HelpScreenButton articlesKey={params?.flow === OnboardingFlow.Pots ? HELP_ARTICLES_KEY.POTS_ONBOARDING : undefined}/>),
        });
    }, [colors.text.primary, params?.flow]);
    const networth = useAppSelector(selectNetworth);
    const annualIncomes = useAppSelector(selectAnnualIncome);
    const dispatch = useAppDispatch();
    useAmplitudeOnMountEvent(params?.flow, !!params?.mountedOnAnotherScreen, 'User Funding Source Screen');
    const navigateToNextScreen = useNavigateToNextScreen();
    const navigateToNextScreenFromPots = useNavigateToNextScreenForPotsOnboarding();
    const onSubmit = useCallback(async (fundingSourceChoiceKey) => {
        if (annualIncomes && networth) {
            const fundingSource = fundingSourceMap[fundingSourceChoiceKey];
            const newParams = {
                annualIncomeMin: annualIncomes.annualIncomeMin,
                annualIncomeMax: annualIncomes.annualIncomeMax,
                liquidNetWorthMin: networth.liquidNetWorthMin,
                liquidNetWorthMax: networth.liquidNetWorthMax,
                fundingSource,
                ...(fundingSource === 'EMPLOYMENT_INCOME' && {
                    employmentStatus: 'EMPLOYED',
                }),
            };
            dispatch({ type: 'SAVE_ONBOARDING_STATE', screen: 'InvestEmploymentStatus', params: newParams });
            if (params?.flow === OnboardingFlow.Pots || params?.flow === OnboardingFlow.Wallet) {
                navigateToNextScreenFromPots();
            }
            else {
                navigateToNextScreen();
            }
        }
        else {
            Sentry.logError({
                filename: 'InvestFundingSource',
                message: 'User not able to go ahead',
            });
            navigation.popToTop();
        }
    }, [annualIncomes, navigateToNextScreen, navigateToNextScreenFromPots, networth, params?.flow]);
    return (<View style={commonStyles.containerNoPadding}>
      <StatusBar barStyle="dark-content" animated/>
      <InvestHeaderWithHelpButton hideButton={!params?.mountedOnAnotherScreen} isPotsOnb={params?.flow === OnboardingFlow.Pots} leftContent={params?.mountedOnAnotherScreen ? <LeaveOnboardingButton flow={params.flow}/> : undefined}/>
      <ScrollView scrollEnabled={false} keyboardShouldPersistTaps="handled" contentContainerStyle={commonStyles.contentContainer}>
        <ScreenInfoHeader title={onboardingTitleStrings.Funding.title} subTitle={onboardingTitleStrings.Funding.subtitle}/>
        <View style={commonStyles.withPadding}>
          <Choices data={CHOICES} onSelect={onSubmit}/>
        </View>
      </ScrollView>
    </View>);
};
export default FundingSourceScreen;
